import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Row,
  Col,
} from "reactstrap";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { FaUser, FaSave, FaTimes } from "react-icons/fa";
import {
  ApiCreateUsuarioNavidad,
  ApiUpdateUsuarioNavidad,
} from "../../../api/navidad";

const UsuarioNavidadForm = ({ isOpen, onClose, onSuccess, user, type }) => {
  const [formData, setFormData] = useState({
    usuario: "",
    code: "",
    nombres: "",
    apellidos: "",
    correo: "",
    telefono: "",
    direccion: "",
    area: "",
    cargo: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (type === "edit" && user) {
        setFormData({
          usuario: user.usuario || "",
          code: user.code || "",
          nombres: user.nombres || "",
          apellidos: user.apellidos || "",
          correo: user.correo || "",
          telefono: user.telefono || "",
          direccion: user.direccion || "",
          area: user.area || "",
          cargo: user.cargo || "",
        });
      } else {
        setFormData({
          usuario: "",
          code: "",
          nombres: "",
          apellidos: "",
          correo: "",
          telefono: "",
          direccion: "",
          area: "",
          cargo: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, type, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const hasUsuario = formData.usuario && formData.usuario.trim() !== "";
    const hasCode = formData.code && formData.code.trim() !== "";

    if (!hasUsuario && !hasCode) {
      newErrors.usuario = "Debe proporcionar al menos Usuario/Cédula o Code";
      newErrors.code = "Debe proporcionar al menos Usuario/Cédula o Code";
    }

    if (!formData.nombres.trim()) {
      newErrors.nombres = "Los nombres son requeridos";
    }

    if (!formData.apellidos.trim()) {
      newErrors.apellidos = "Los apellidos son requeridos";
    }

    if (formData.correo && !/\S+@\S+\.\S+/.test(formData.correo)) {
      newErrors.correo = "El email no tiene un formato válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Por favor corrige los errores en el formulario");
      return;
    }

    setLoading(true);
    try {
      let result;

      const dataToSend = { ...formData };
      if (!dataToSend.code || dataToSend.code.trim() === "") {
        delete dataToSend.code;
      }

      if (type === "edit") {
        result = await ApiUpdateUsuarioNavidad({
          pre_usuario_navidad_id: user.pre_usuario_navidad_id,
          ...dataToSend,
        });
      } else {
        result = await ApiCreateUsuarioNavidad(dataToSend);
      }

      if (result.sms === "ok") {
        toast.success(
          type === "edit"
            ? "Usuario actualizado correctamente"
            : "Usuario creado correctamente"
        );
        onSuccess();
        onClose();
      } else {
        toast.error(result.mensaje || "Error al procesar la solicitud");
      }
    } catch (error) {
      toast.error("Error al procesar la solicitud");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={handleClose}>
        <div className="d-flex align-items-center">
          <FaUser className="me-2 text-primary" />
          {type === "edit" ? "Editar Usuario Navidad" : "Nuevo Usuario Navidad"}
        </div>
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="usuario" className="fw-bold">
                    Usuario/Cédula <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="usuario"
                    name="usuario"
                    value={formData.usuario}
                    onChange={handleChange}
                    invalid={!!errors.usuario}
                    placeholder="Ej: 1234567890"
                    disabled={loading}
                  />
                  {errors.usuario && (
                    <div className="invalid-feedback d-block">
                      {errors.usuario}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="code" className="fw-bold">
                    Código <small className="text-muted">(Opcional)</small>
                  </Label>
                  <Input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    invalid={!!errors.code}
                    placeholder="Ej: EMP001"
                    disabled={loading}
                  />
                  {errors.code && (
                    <div className="invalid-feedback d-block">
                      {errors.code}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="nombres" className="fw-bold">
                    Nombres <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="nombres"
                    name="nombres"
                    value={formData.nombres}
                    onChange={handleChange}
                    invalid={!!errors.nombres}
                    placeholder="Ej: Juan Carlos"
                    disabled={loading}
                  />
                  {errors.nombres && (
                    <div className="invalid-feedback d-block">
                      {errors.nombres}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="apellidos" className="fw-bold">
                    Apellidos <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="apellidos"
                    name="apellidos"
                    value={formData.apellidos}
                    onChange={handleChange}
                    invalid={!!errors.apellidos}
                    placeholder="Ej: García López"
                    disabled={loading}
                  />
                  {errors.apellidos && (
                    <div className="invalid-feedback d-block">
                      {errors.apellidos}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="cargo">Cargo</Label>
                  <Input
                    type="text"
                    id="cargo"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleChange}
                    placeholder="Ej: Gerente"
                    disabled={loading}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="area">Área</Label>
                  <Input
                    type="text"
                    id="area"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    placeholder="Ej: Administración"
                    disabled={loading}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="correo">Email</Label>
                  <Input
                    type="email"
                    id="correo"
                    name="correo"
                    value={formData.correo}
                    onChange={handleChange}
                    invalid={!!errors.correo}
                    placeholder="Ej: usuario@empresa.com"
                    disabled={loading}
                  />
                  {errors.correo && (
                    <div className="invalid-feedback d-block">
                      {errors.correo}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="telefono">Teléfono</Label>
                  <Input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="Ej: 0987654321"
                    disabled={loading}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="direccion">Dirección</Label>
              <Input
                type="textarea"
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Ej: Av. Principal 123, Quito"
                rows={2}
                disabled={loading}
              />
            </FormGroup>

            {type === "edit" && (
              <Alert color="info">
                <strong>Nota:</strong> Al editar un usuario existente, se
                mantendrán sus canastas asignadas.
              </Alert>
            )}
          </motion.div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="outline-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            <FaTimes className="me-1" />
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading}
            className="d-flex align-items-center"
          >
            {loading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Guardando...</span>
                </div>
                Guardando...
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                {type === "edit" ? "Actualizar" : "Crear"} Usuario
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UsuarioNavidadForm;
